import { ActivityDoc, type InboxFilter } from "@kwp/contracts";
import { reviveTimestamps } from "@kwp/firestore";
import type { Thread } from "./model";
import type { Scope } from "./scope";

/**
 * Chats and tasks in the inbox (WP-054, architecture §11): `users/{me}/activity` rows, one per
 * conversation or task that involves me, mixed with mail by time.
 */
export type ActivityItem = ActivityDoc & { id: string };

export function toActivity(id: string, data: unknown): ActivityItem | null {
  const parsed = ActivityDoc.safeParse(reviveTimestamps(data));
  return parsed.success ? { ...parsed.data, id } : null;
}

/** Which kind of row the inbox shows; no kind shows all three. */
export const KINDS = [
  { key: "mail", label: "Mail" },
  { key: "chat", label: "Chat" },
  { key: "task", label: "Tasks" },
] as const;
export type InboxKind = (typeof KINDS)[number]["key"];

/**
 * Unread while something happened after I last read it: in the inbox (`readAt`) or, for a chat,
 * in Chat itself (my read marker in that channel), so reading it in either place clears both.
 */
export function isUnreadActivity(item: ActivityItem, chatReadAt: Date | null | undefined): boolean {
  const seen = [item.readAt, item.kind === "chat" && chatReadAt !== undefined ? chatReadAt : null].filter((d): d is Date => d !== null);
  return seen.every((d) => d.getTime() < item.lastAt.getTime());
}

/** The rows a view shows: none in a search, an account scope or a mail-only filter. */
export function activityInView(
  items: readonly ActivityItem[],
  view: { scope: Scope; filter: InboxFilter | undefined; kind: InboxKind | undefined; unread: (item: ActivityItem) => boolean },
): ActivityItem[] {
  if (view.kind === "mail" || view.scope.kind === "accounts") return [];
  if (view.filter !== undefined && view.filter !== "unread") return [];
  const ids = view.scope.kind === "workspaces" ? new Set(view.scope.ids) : null;
  return items.filter(
    (i) => (ids === null || ids.has(i.workspaceId)) && (view.kind === undefined || i.kind === view.kind) && (view.filter !== "unread" || view.unread(i)),
  );
}

export type Entry = { kind: "thread"; id: string; at: Date; thread: Thread } | { kind: "activity"; id: string; at: Date; item: ActivityItem };

/**
 * Mail and activity in one list, newest first. While older mail is still to load, activity older
 * than the oldest loaded conversation waits for it, so scrolling down never jumps back in time.
 */
export function mergeEntries(threads: readonly Thread[], items: readonly ActivityItem[], moreMail: boolean): Entry[] {
  const oldest = threads[threads.length - 1];
  const cutoff = moreMail && oldest !== undefined ? oldest.lastMessageAt.getTime() : null;
  const rows = items.filter((i) => cutoff === null || i.lastAt.getTime() >= cutoff).sort((a, b) => b.lastAt.getTime() - a.lastAt.getTime());
  // Mail keeps its own order (the Snoozed view is not by time); activity slots in by time.
  const out: Entry[] = [];
  let next = 0;
  for (const t of threads) {
    for (let r = rows[next]; r !== undefined && r.lastAt.getTime() > t.lastMessageAt.getTime(); r = rows[++next]) out.push({ kind: "activity", id: r.id, at: r.lastAt, item: r });
    out.push({ kind: "thread", id: t.id, at: t.lastMessageAt, thread: t });
  }
  for (const r of rows.slice(next)) out.push({ kind: "activity", id: r.id, at: r.lastAt, item: r });
  return out;
}

export const threadOf = (entry: Entry | undefined): Thread | undefined => (entry !== undefined && entry.kind === "thread" ? entry.thread : undefined);

const FIELD_LABEL: Record<ActivityItem["changes"][number]["field"], string> = {
  title: "Title",
  notes: "Notes",
  due: "Due",
  assignee: "Assignee",
  status: "Status",
  visibility: "Visibility",
};

const STATUS_LABEL: Record<string, string> = { open: "Open", done: "Done", cancelled: "Cancelled" };

/** A changed field's value as people read it: dates as dates, people by name, statuses in words. */
export function changeValue(field: ActivityItem["changes"][number]["field"], value: string | null, nameOf: (uid: string) => string): string {
  if (value === null) return field === "assignee" ? "Unassigned" : field === "due" ? "No date" : "(empty)";
  if (field === "due") return new Date(value).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  if (field === "assignee") return nameOf(value);
  if (field === "status") return STATUS_LABEL[value] ?? value;
  if (field === "visibility") return value === "private" ? "Private" : "Shared";
  return value;
}

export const fieldLabel = (field: ActivityItem["changes"][number]["field"]) => FIELD_LABEL[field];

/** The row's second line: what happened, in a few words. */
export function eventText(item: ActivityItem): string {
  switch (item.event) {
    case "dm":
      return "Direct message";
    case "mention":
      return `Mentioned you in #${item.title}`;
    case "message_edited":
      return item.title === "" ? "Edited a direct message" : `Edited a message in #${item.title}`;
    case "task_assigned":
      return "Assigned you a task";
    case "task_changed":
      return "Changed a task";
  }
}
